/**
 * DifficultyBar — the shared, presentational difficulty selector used by every
 * editor (Synth VR, Flow, Console). It owns ALL the visual/layout/styling and
 * knows nothing about any store: each editor builds a `DifficultyBarProps`
 * model from its own state and passes it in.
 *
 * Natural per-editor differences are expressed as data, not forks:
 *   - difficulty names/counts come from the editor (its own nomenclature),
 *   - the optional `lanes` control (3/4 lanes) is only passed by Flow.
 */

import React from 'react';
import { color, font } from './theme';

/** One difficulty entry. `present` = already exists in the map (vs. createable). */
export interface DiffItem {
  id: string;
  label: string;
  count: number;
  present: boolean;
}

/** Optional lane-count toggle (Flow only). */
export interface LanesControl {
  value: number;
  options: number[];
  onChange: (n: number) => void;
}

export interface DifficultyBarProps {
  items: DiffItem[];
  activeId: string;
  /** A present difficulty was clicked. */
  onSelect: (id: string) => void;
  /** A missing default was clicked — the editor decides what "create" means. */
  onCreate: (id: string) => void;
  /** "+ Add" clicked — the editor prompts for a custom name. */
  onAddCustom: () => void;
  /** Lane-count variant; omit to hide the row (VR / Console). */
  lanes?: LanesControl;
}

export default function DifficultyBar({
  items, activeId, onSelect, onCreate, onAddCustom, lanes,
}: DifficultyBarProps) {
  return (
    <div style={gridStyle}>
      {items.map(d => {
        const active = d.id === activeId;
        return (
          <button
            key={d.id}
            onClick={() => (d.present ? onSelect(d.id) : onCreate(d.id))}
            title={d.present ? `${d.label} — ${d.count} notes` : `Create ${d.label}`}
            style={{
              ...btnStyle,
              opacity: d.present ? 1 : 0.5,
              borderColor: active ? 'var(--accent)' : LINE,
              color: active ? 'var(--accent)' : color.inkDim,
              background: active ? 'var(--tint-accent-med)' : INACTIVE_BG,
            }}
          >
            <span>{d.label}</span>
            <span style={{ ...countPillStyle, color: active ? 'var(--accent)' : color.inkFaint }}>
              {d.present ? d.count : '+'}
            </span>
          </button>
        );
      })}
      <button
        onClick={onAddCustom}
        title="Add a custom-named difficulty"
        style={{ ...btnStyle, borderColor: LINE, color: color.inkDim, background: INACTIVE_BG }}
      >
        <span>+ Add</span>
      </button>

      {lanes && (
        <div style={lanesRowStyle}>
          <span style={lanesLabelStyle}>Lanes</span>
          {lanes.options.map(n => {
            const active = lanes.value === n;
            return (
              <button
                key={n}
                onClick={() => lanes.onChange(n)}
                style={{
                  ...laneBtnStyle,
                  borderColor: active ? 'var(--accent)' : LINE,
                  color: active ? 'var(--accent)' : color.inkDim,
                  background: active ? 'var(--tint-accent-med)' : INACTIVE_BG,
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const LINE = 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)';
const INACTIVE_BG = 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)';

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 4,
};

const btnStyle: React.CSSProperties = {
  // Single row: difficulty name + a small count pill, so the panel is compact.
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid',
  cursor: 'pointer',
  fontFamily: font.sans,
  fontSize: 11,
  fontWeight: 600,
  transition: 'all 0.15s ease',
};

const countPillStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  padding: '2px 6px',
  borderRadius: 999,
  background: 'oklch(0.10 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.65)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
};

// Lanes row spans both columns, with a left label and the option buttons.
const lanesRowStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 2,
};

const lanesLabelStyle: React.CSSProperties = {
  flexShrink: 0,
  marginRight: 'auto',
  paddingLeft: 4,
  fontSize: 11,
  fontWeight: 600,
  color: color.inkDim,
  fontFamily: font.sans,
};

const laneBtnStyle: React.CSSProperties = {
  minWidth: 44,
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid',
  cursor: 'pointer',
  fontFamily: font.sans,
  fontSize: 11,
  fontWeight: 700,
  transition: 'all 0.15s ease',
};
